import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';

import logger from '../utils/logger';

interface RegistrationStatus {
  isOpen: boolean;
  isClosed: boolean;
  isExtended: boolean;
  status: 'not_yet_open' | 'open' | 'extended' | 'closed';
  deadline: string;
  originalDeadline: string;
  hasExtension: boolean;
  extension: {
    _id: string;
    extendedDeadline: string;
    reason: string;
    createdAt: string;
  } | null;
  loading: boolean;
  error: string | null;
}

export const useRegistrationStatus = (competitionId?: string) => {
  const { clubId } = useAuth();
  const [status, setStatus] = useState<RegistrationStatus>({
    isOpen: false,
    isClosed: true,
    isExtended: false,
    status: 'closed',
    deadline: '',
    originalDeadline: '',
    hasExtension: false,
    extension: null,
    loading: true,
    error: null
  });

  const checkRegistrationStatus = useCallback(async () => {
    if (!competitionId || !clubId) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }));
      
      // Check registration status including extensions
      const response = await axios.get(
        `/api/competitions/${competitionId}/registration-status/me`
      );
      
      const data = response.data;
      
      setStatus({
        isOpen: data.isOpen,
        isClosed: !data.isOpen,
        isExtended: data.status === 'extended',
        status: data.status,
        deadline: data.deadline,
        originalDeadline: data.originalDeadline,
        hasExtension: data.hasExtension,
        extension: data.extension,
        loading: false,
        error: null
      });
      
    } catch (error: any) {
      //console.error('Error checking registration status:', error);
      logger.error('Error checking registration status:', { error });
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Fehler beim Prüfen des Meldestatus'
      }));
    }
  }, [competitionId, clubId]);

  useEffect(() => {
    checkRegistrationStatus();
  }, [checkRegistrationStatus]);

  return {
    ...status,
    refresh: checkRegistrationStatus
  };
};

// Utility function for formatting deadline messages
export const getDeadlineMessage = (status: RegistrationStatus): string => {
  if (status.loading) return 'Lade Meldestatus...';
  if (status.error) return status.error;
  
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('de-DE');
  
  switch (status.status) {
    case 'not_yet_open':
      return `Meldung noch nicht geöffnet. Meldeschluss: ${formatDate(status.originalDeadline)}`;
    
    case 'open':
      return `Meldung offen bis: ${formatDate(status.deadline)}`;
    
    case 'extended':
      return `Meldung verlängert bis: ${formatDate(status.deadline)} (ursprünglich: ${formatDate(status.originalDeadline)})`;
    
    case 'closed':
      return `Meldeschluss war: ${formatDate(status.originalDeadline)}`;
    
    default:
      return 'Meldestatus unbekannt';
  }
};